import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";

import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CancelIcon from '@mui/icons-material/Cancel';
import Divider from "@mui/material/Divider";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from "@mui/material/IconButton";
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Table from '../helpers/Table'
import DeletePopup from "../helpers/DeletePopup";

import { addDriver, editDriver, deleteDriver, clearQueryResult } from "../../actions/valueActions";

import { formatString, convertBoolDiffValue } from "./valueFunctions";
// import firstCharToUpper from "../helpers/stringFormat";
import CostValueDriverOperatingForm from "./CostValueDriverOperatingForm";
import { getIconFromDriverName } from "./iconFunctions";
import ModalElement from "../Modal";
import AddOperatingCostData from "./AddOperatingCostData";
import { toolTipUserIcon } from "./iconFunctions";
import { getAllOutcomes } from "./valueFunctions";

const CostValueDriverOverhead = ({ id, valueModel, costs, driver, outcome }) => {
    const formikRef = useRef(null);
    const dispatch = useDispatch();

    const [open, setOpen] = React.useState(false);
    const [selectedCost, setSelectedCost] = useState(Object.keys(costs)[0])
    const [deleteDriverPopupOpen, setDeleteDriverPopupOpen] = useState(false);
    const [deleteDriverId, setDeleteDriverId] = useState(null);
    const [editDriverId, setEditDriverId] = useState(null);
    const [addCostModalOpen, setAddCostModalOpen] = useState(false);

    const driverMapping = {
        finance_and_accounting: {driverPath: 'accounting_savings', type: 'accounting', name: 'Finance and Accounting'},
        HR_and_people: {driverPath: 'hr_savings', type: 'hr', name: 'HR and People'},
        sales_and_business_development: {driverPath: 'sales_savings', type: 'bd', name: 'Sales and Business Development'},
        marketing_and_communications: {driverPath: 'marketing_savings', type: 'marcom', name: 'Marketing and Communications'},
        customer_success: {driverPath: 'customer_success_savings', type: 'customer_success', name: 'Customer Success'},
        IT_and_infrastructure: {driverPath: 'it_savings', type: 'it', name: 'IT and Infrastructure'},
        legal_and_compliance: {driverPath: 'legal_savings', type: 'legal', name: 'Legal and Compliance'},
        procurement_and_purchasing: {driverPath: 'procurement_savings', type: 'procurement', name: 'Procurement and Purchasing'},
    }

    const handleClose = () => {
        setOpen(false);
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleCostChange = (event) => {
        setSelectedCost(event.target.value)
        if (editDriverId) {
            setEditDriverId(null)
            if (formikRef.current) {
                formikRef.current.resetForm();
            }
        }
    }

    const openDeleteDriver = (id) => {
        setDeleteDriverId(id)
        setDeleteDriverPopupOpen(true)
    }

    const editDriver = (id) => {
        setEditDriverId(id)
        let driver = valueModel.cost_value_driver[driverMapping[driver].driverPath].find(driver => driver.id === id)
        setSelectedCost(driver.sub_cost)
    }

    const toggleAddCostModalOpen = () => {
        setAddCostModalOpen(!addCostModalOpen)
        dispatch(clearQueryResult())
    }

    function renderAddCostModal() {
        return (
            <ModalElement
                modalOpen={addCostModalOpen}
                modalWidth={1000}
                handleClose={toggleAddCostModalOpen}
                header={`Add ${driverMapping[driver].name} Costs`}
                instruction="Please specify what you would like to add and provide a wish that will bring some context to your request"
                content={
                    <AddOperatingCostData
                        id={id}
                        industry={valueModel?.industry}
                        type={driverMapping[driver].type}
                        cost_category={driver}
                        costTypes={costs}
                        filterCosts={[]}
                        handleClose={toggleAddCostModalOpen}
                    />
                }
            />

        )
    }

    const onSubmitDriver = async (values, driver, cost, { setSubmitting, setErrors }) => {
        values.id = editDriverId
        if (outcome) {
            values.outcome = outcome
        }
        values.cost = driver
        values.sub_cost = cost
        values.multiple_value = values.multiple_value.toString().replace(/,/g, "");
        let success = false

        if (editDriverId) {
            const result = await dispatch(editDriver(driverMapping[driver].type, id, values, { setSubmitting, setErrors }))
            success = result?.success;
            if (success) {
                setEditDriverId(null)
            }
        }
        else {
            const result = await dispatch(addDriver(driverMapping[driver].type, id, values, { setSubmitting, setErrors }))
            success = result?.success;
        }

        if (formikRef.current && success) {
            formikRef.current.resetForm();
        }
    }

    const handleReset = () => {
        setEditDriverId(null)

        // Access the formikRef current value and call resetForm
        if (formikRef.current) {
            formikRef.current.resetForm();
        }
    };

    const driverColumns = [
        { id: 'outcome', label: 'Outcome', minWidth: 170, action: false },
        { id: 'driver_name', label: 'Name', minWidth: 170, action: false },
        // { id: 'sub_cost', label: 'Cost', format: (value) => formatString(value), minWidth: 150, action: false },
        // { id: 'multiple_type', label: 'Type', format: (value) => firstCharToUpper(value), minWidth: 150, action: false },
        { id: 'efficiency_gain', label: 'Efficiency', minWidth: 100, action: false },
        { id: 'share_of_cost', label: '% of cost', format: (value) => value + ' %', minWidth: 100, action: false },
        { id: 'positive_driver', label: 'Differentiation value', minWidth: 100, format: (value) => convertBoolDiffValue(Boolean(value)), action: false },
        { id: 'actions', label: 'Actions', minWidth: 100, action: false },
    ];

    const createDriverData = (id, outcome, driver_name, sub_cost, multiple_type, efficiency_gain, share_of_cost, positive_driver, actions) => {
        efficiency_gain = `${efficiency_gain} % ${positive_driver ? 'gain' : 'loss'}`
        return { id, outcome, driver_name, sub_cost, multiple_type, efficiency_gain, share_of_cost, positive_driver, actions };
    }

    const driverActions = (id) => {
        return (
            <React.Fragment>
                <IconButton size="small" onClick={() => editDriver(id)}>
                    <EditIcon />
                </IconButton>
                <IconButton size="small" onClick={() => openDeleteDriver(id)}>
                    <DeleteIcon />
                </IconButton>
            </React.Fragment>
        )
    }

    const listForThisDriver =
        valueModel?.cost_value_driver?.[driverMapping[driver].driverPath] || [];

    const driverRows = listForThisDriver.filter(driver => !outcome || driver.outcome === outcome).map((driver) => {
        return (
            createDriverData(driver.id, driver.outcome, driver.driver_name, driver.sub_cost, driver.multiple_type, driver.efficiency_gain, driver.share_of_cost, driver.positive_driver, driverActions(driver.id))
        )
    })

    const driverTable = () => {
        return (
            <React.Fragment>
                <Divider />
                <Table
                    columns={driverColumns}
                    rows={driverRows}
                    selectedId={editDriverId}
                />
            </React.Fragment>
        )
    }

    const driverFormActions = () => {
        return (
            <React.Fragment>
                {editDriverId ? null
                    : <Button variant="contained" startIcon={<Box component={getIconFromDriverName(driver)} />} size="small" type="submit">Add driver</Button>
                }
                {editDriverId ?
                    <Stack direction="row" spacing={2}>
                        <Button variant="contained" startIcon={<EditIcon />} size="small" type="submit">Save</Button>
                        <Button variant="contained" startIcon={<CancelIcon />} size="small" onClick={handleReset}>Cancel</Button>
                    </Stack>
                    : null
                }
            </React.Fragment>
        )
    }

    return (
        <Box>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Stack gap={1} direction="row" sx={{ ml: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                        Driver type:
                    </Typography>
                    <Typography variant="caption">
                        Operating Costs Savings
                    </Typography>
                </Stack>
                <Box sx={{ width: '30%' }} />
                <Button startIcon={<AutoFixHighIcon />} onClick={toggleAddCostModalOpen} sx={{ textTransform: 'none' }}>Add Costs</Button>
            </Box>
            <Stack direction="column" gap={1} sx={{ ml: 2, mt: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    1. Select Cost Type
                </Typography>
                <Box>
                    <Select
                        open={open}
                        onClose={handleClose}
                        onOpen={handleOpen}
                        value={selectedCost}
                        onChange={handleCostChange}
                        size='small'
                    >
                        {Object.keys(costs).map((cost) => (
                            <MenuItem key={cost} value={cost}>
                                {/* {formatString(cost)} */}
                                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                    {formatString(cost)}
                                    {costs[cost]?.user_created && toolTipUserIcon()}
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            </Stack>

            <CostValueDriverOperatingForm
                currency="€"
                outcome={outcome}
                outcomeList={getAllOutcomes(valueModel?.driver_analysis)}
                costStructure={costs[selectedCost]}
                editOperatingDriverId={editDriverId}
                editOperatingDriver={listForThisDriver.find(driver => driver.id === editDriverId)}
                formActions={driverFormActions}
                onSubmit={(values, { setSubmitting, setErrors }) => onSubmitDriver(values, driver, selectedCost, { setSubmitting, setErrors })}
                ref={formikRef}
            />
            {driverTable()}
            <DeletePopup open={deleteDriverPopupOpen} onClose={() => setDeleteDriverPopupOpen(false)} onDelete={() => dispatch(deleteDriver('randd', id, deleteDriverId))} />
            {renderAddCostModal()}
        </Box>
    )
}

export default CostValueDriverOverhead